import {
  jastipFeeConfigSchema,
  jastipFeeWireSchema,
  MAX_FEE_RUPIAH,
  type FeeTier,
  type JastipFeeConfig,
  type JastipFeeTierWire,
  type JastipFeeWire,
} from './schema'

/**
 * The BE requires `max_price` on every tier, but the design's last range is
 * open-ended ("dan seterusnya"). Until the BE accepts `null`, the last range is
 * sent with the app's highest item price and read back as open-ended.
 */
export const OPEN_ENDED_MAX_PRICE = MAX_FEE_RUPIAH

const WIRE_METHOD = {
  custom: 'CUSTOM',
  flat: 'FLAT',
  percentage: 'PERCENTAGE',
  tier: 'TIER',
} as const

function tierToWire(tier: FeeTier, isLast: boolean): JastipFeeTierWire {
  return {
    min_price: tier.start,
    max_price: isLast ? OPEN_ENDED_MAX_PRICE : tier.end,
    type: tier.type === 'percent' ? 'PERCENTAGE' : 'FLAT',
    value: tier.value,
  }
}

/** FE config → PUT body `jastip_fee`. Only the fields valid for the method are sent. */
export function toWire(config: JastipFeeConfig): JastipFeeWire {
  const method = WIRE_METHOD[config.method]
  switch (config.method) {
    case 'custom':
    case 'flat':
      return { method, amount: config.amount }
    case 'percentage':
      return { method, percentage: config.percent, minimum_fee: config.minimum }
    case 'tier':
      return {
        method,
        tiers: config.tiers.map((tier, index) =>
          tierToWire(tier, index === config.tiers.length - 1),
        ),
      }
  }
}

/**
 * The FE always treats the last range as open-ended ("dan seterusnya"), so a
 * finite BE `max_price` on it is read as open-ended and saved back as
 * `OPEN_ENDED_MAX_PRICE`. Otherwise the form would show unsaved changes on load.
 */
function tierFromWire(tier: JastipFeeTierWire, isLast: boolean) {
  return {
    start: tier.min_price,
    end: isLast ? null : (tier.max_price ?? null),
    type: tier.type?.trim().toUpperCase() === 'PERCENTAGE' ? 'percent' : 'amount',
    value: tier.value,
  }
}

function candidate(wire: JastipFeeWire) {
  switch (wire.method?.trim().toUpperCase()) {
    case 'CUSTOM':
      return { method: 'custom', amount: wire.amount ?? 0 }
    case 'FLAT':
      return { method: 'flat', amount: wire.amount }
    case 'PERCENTAGE':
      return { method: 'percentage', percent: wire.percentage, minimum: wire.minimum_fee }
    case 'TIER': {
      const tiers = wire.tiers ?? []
      return {
        method: 'tier',
        tiers: tiers.map((tier, index) => tierFromWire(tier, index === tiers.length - 1)),
      }
    }
    default:
      return null
  }
}

/**
 * GET `jastip_fee` → FE config. Returns `null` when the stored shape is missing
 * or incomplete, so the caller can fall back to the Custom Rp0 default.
 */
export function fromWire(raw: unknown): JastipFeeConfig | null {
  const wire = jastipFeeWireSchema.safeParse(raw)
  if (!wire.success) return null
  const parsed = jastipFeeConfigSchema.safeParse(candidate(wire.data))
  return parsed.success ? parsed.data : null
}
